import type { ReactElement } from "react";

import { Text } from "@/components/Text";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import { profileHighlightGroups } from "@/lib/constants";
import { keywords as keywordUtils } from "@/lib/keywords";
import { cn } from "@/lib/utils";
import { ClassifiedKeyword } from "@/types";

const MAX_KEYWORD_PHRASE_WORDS = 3;

// Tooltip border + label tint matching each highlight color's text tone.
const colorToTooltipClassName: Record<
  NonNullable<ClassifiedKeyword["color"]>,
  string
> = {
  blue: "border-blue-700 text-blue-700",
  violet: "border-violet-700 text-violet-700",
  teal: "border-teal-700 text-teal-700",
  orange: "border-orange-700 text-orange-700",
  green: "border-green-700 text-green-700",
  slate: "border-slate-700 text-slate-700",
  red: "border-red-600 text-red-600",
};

// Uncategorized keywords (e.g. search-result matches) fall back to blue with
// no tooltip.
const DEFAULT_HIGHLIGHT_CLASSNAME = "border-blue-100 bg-blue-50 text-blue-700";

// Wraps fuzzy matches of 1-3 word keyword phrases in the Search V2 treatment.
export const KeywordHighlightedText = ({
  highlightKeywords,
  text,
}: {
  highlightKeywords?: ClassifiedKeyword[];
  text: string;
}) => {
  /* Helpers */
  const keywordPatterns = highlightKeywords
    ?.map((classifiedKeyword) => ({
      ...classifiedKeyword,
      words: classifiedKeyword.keyword
        .split(/\s+/)
        .map(keywordUtils.normalize)
        .filter(Boolean),
    }))
    .filter(({ words }) => words.length);

  const getHighlightedNodes = () => {
    if (!keywordPatterns?.length) return [text];

    const textWords = text.split(/(\s+)/); // keep whitespace tokens for reassembly
    const nodes: (string | ReactElement)[] = [];

    for (let i = 0; i < textWords.length; i++) {
      let matchedTokenCount = 0;
      // Try 3-word phrases first so longer matches win. Word tokens are every
      // other entry (whitespace interleaved), so a phrase of n words spans
      // 2n - 1 tokens.
      for (
        let phraseSize = MAX_KEYWORD_PHRASE_WORDS;
        phraseSize >= 1 && !matchedTokenCount;
        phraseSize--
      ) {
        const tokenCount = phraseSize * 2 - 1;
        const window = textWords
          .slice(i, i + tokenCount)
          .map(keywordUtils.normalize)
          .filter(Boolean);
        if (window.length !== phraseSize) continue;

        const matchedKeyword = keywordPatterns.find(({ words }) =>
          keywordUtils.isFuzzyMatch(window.join(" "), words.join(" ")),
        );
        if (!matchedKeyword) continue;

        const highlightGroup = profileHighlightGroups.find(
          ({ type }) => type === matchedKeyword.category,
        );

        const mark = (
          <mark
            className={cn(
              "mx-0.5 inline cursor-default rounded-md border px-1.5 py-0.5 text-[0.95em] font-normal",
              highlightGroup?.className ?? DEFAULT_HIGHLIGHT_CLASSNAME,
            )}
          >
            {textWords.slice(i, i + tokenCount).join("")}
          </mark>
        );

        nodes.push(
          matchedKeyword.category ? (
            <Tooltip key={i}>
              <TooltipTrigger asChild>{mark}</TooltipTrigger>
              <TooltipContent
                className={cn(
                  "bg-white px-4 py-3 shadow-md",
                  colorToTooltipClassName[matchedKeyword.color ?? "slate"],
                )}
              >
                <Text className="font-plex-mono text-[11px] font-medium uppercase tracking-wider text-slate-500">
                  {matchedKeyword.category}
                </Text>
              </TooltipContent>
            </Tooltip>
          ) : (
            <span key={i}>{mark}</span>
          ),
        );
        matchedTokenCount = tokenCount;
      }

      if (matchedTokenCount) {
        i += matchedTokenCount - 1;
      } else {
        nodes.push(textWords[i]);
      }
    }

    return nodes;
  };

  /* Render */
  const render = () => {
    return (
      <TooltipProvider delayDuration={150}>
        {getHighlightedNodes()}
      </TooltipProvider>
    );
  };

  return render();
};
